import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { InlineSvg } from "../components/InlineSvg";
import { MascotImage } from "../components/MascotImage";
import { PreviewBadge } from "../components/PreviewNotice";
import { Coachmark } from "../components/Coachmark";
import { useEffect, useState } from "react";
import { AlertTriangle, ArrowRight, Check, Lock, Zap } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAccountHistory, useProject } from "../lib/projects";
import { ApiError } from "../lib/api";
import {
  actionAllowance,
  billingLimitToast,
  freePausedMessage,
} from "../lib/entitlement";
import { moderationErrorToast } from "../lib/moderation";
import { useSubscription } from "../lib/useSubscription";
import { ContentRulesNote } from "../components/ContentRulesNote";
import { MascotArt } from "../components/MascotArt";
import { PromptComposer } from "../components/PromptComposer";
import { useWorkspace, type Workspace } from "../lib/useWorkspace";
import {
  VersionGrid,
  MobileVersionBar,
} from "../components/workspace/VersionSidebar";
import { WorkspaceToolbar } from "../components/workspace/WorkspaceToolbar";
import { Inspector, InspectorSection } from "../components/workspace/Inspector";
import { VariationsPhaseLayout } from "../components/workspace/VariationsPhaseLayout";
import { WORKSPACE_HEIGHT } from "../components/workspace/layout";
import { Button, Spinner, Sticker, toastManager } from "../components/ui";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPopup,
  DialogTitle,
} from "../components/ui/dialog";

export const Route = createFileRoute("/projects/$id")({
  component: ProjectWorkspace,
});

const STAGES = [
  { status: "queued", label: "In the queue" },
  { status: "generating", label: "Drawing your character" },
  { status: "vectorizing", label: "Turning it into clean vectors" },
] as const;

const PROGRESS: Record<string, number> = {
  queued: 8,
  generating: 40,
  vectorizing: 75,
  done: 100,
  failed: 100,
};

function ProjectWorkspace() {
  const { id } = useParams({ strict: false }) as { id: string };
  const { data: project, isLoading, error } = useProject(id);

  if (isLoading) {
    return <LoadingShell />;
  }
  if (error || !project) {
    return <NotFoundShell />;
  }

  // The route is a phase router: a locked base means the project has
  // moved past iteration and the variations phase takes over inline.
  // There is no longer a separate /projects/$id/variations URL.
  if (project.base_job_id) {
    return <VariationsPhaseLayout projectId={id} />;
  }

  return <CharacterPhase projectId={id} />;
}

function CharacterPhase({ projectId }: { projectId: string }) {
  const { data: project } = useProject(projectId);
  if (!project) return <LoadingShell />;
  const ws = useWorkspace(project);
  const history = useAccountHistory(projectId);

  if (!ws.hydrated) return <LoadingShell />;

  return (
    <div className={cn("flex", WORKSPACE_HEIGHT)}>
      <div className="flex min-w-0 flex-1 flex-col">
        <WorkspaceToolbar
          projectId={project.id}
          name={project.name}
          phase="design"
          style={project.style}
        />
        <MobileVersionBar ws={ws} />
        <PreviewCanvas ws={ws} />
        <PromptBar ws={ws} />
      </div>
      <DesignInspector ws={ws} />
      <Coachmark
        id="lock-in"
        target="lock-in"
        text="Like it? Make it your mascot. Not quite? Describe the changes below and draw again."
        when={
          history?.locked === false &&
          ws.activeJob?.status === "done" &&
          ws.baseJobId !== ws.activeRevisionId
        }
      />
    </div>
  );
}

const STATUS_TEXT: Record<string, string> = {
  queued: "In the queue…",
  generating: "Drawing…",
  vectorizing: "Vectorizing…",
  done: "Ready",
  failed: "Didn't work out",
};

function DesignInspector({ ws }: { ws: Workspace }) {
  const number = activeNumber(ws);
  const status = ws.activeJob?.status ?? (ws.isCreating ? "queued" : null);

  return (
    <Inspector>
      <InspectorSection
        title="Selected version"
        aside={
          ws.activeRevision ? (
            <Sticker tone="lilac" size="sm">
              V{number}
            </Sticker>
          ) : null
        }
      >
        {ws.activeRevision ? (
          <>
            <p className="text-sm font-bold text-ink">
              “{ws.activeRevision.prompt}”
            </p>
            {status && (
              <p
                className={cn(
                  "mt-2 inline-flex items-center gap-1.5 text-xs font-extrabold",
                  status === "done"
                    ? "text-success-foreground"
                    : status === "failed"
                      ? "text-destructive-foreground"
                      : "text-ink-soft"
                )}
              >
                {status === "done" ? (
                  <Check className="size-3.5" strokeWidth={3} />
                ) : status !== "failed" ? (
                  <Spinner className="size-3.5" />
                ) : null}
                {STATUS_TEXT[status]}
              </p>
            )}
            {status === "done" && ws.baseJobId !== ws.activeRevisionId && (
              <div className="mt-4">
                <LockInButton ws={ws} number={number} className="w-full" />
                <p className="mt-2.5 text-xs font-semibold text-ink-soft">
                  Happy with it? Make it your mascot, then build its pack of
                  poses and expressions.
                </p>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm font-semibold text-ink-soft">
            Describe your character below to draw the first version.
          </p>
        )}
      </InspectorSection>

      <InspectorSection
        title="History"
        aside={
          <span className="text-xs font-bold text-ink-faint">
            {ws.revisions.length}
          </span>
        }
      >
        <VersionGrid ws={ws} />
      </InspectorSection>
    </Inspector>
  );
}

function activeNumber(ws: Workspace) {
  return ws.revisions.findIndex((r) => r.id === ws.activeRevisionId) + 1;
}

function PreviewCanvas({ ws }: { ws: Workspace }) {
  const {
    activeRevision,
    activeJob,
    svg,
    previewImageUrl,
    svgLoading,
    isCreating,
    previewOnly,
  } = ws;

  const hasVersion = !!activeRevision;
  const isDone = activeJob?.status === "done";
  const isFailed = activeJob?.status === "failed";
  const status = activeJob?.status ?? "queued";
  const generating = isCreating || (hasVersion && !isDone && !isFailed);
  const number = activeRevision ? activeNumber(ws) : undefined;

  return (
    <div className="dot-grid relative min-h-0 flex-1 overflow-auto">
      <div className="flex h-full min-h-[260px] flex-col items-center justify-center gap-3 p-3 sm:min-h-[340px] sm:gap-4 sm:p-8">
        <div className="tactile relative flex min-h-0 w-full max-w-2xl flex-1 flex-col gap-2 p-2">
          <div className="checker-bg flex min-h-[200px] w-full flex-1 items-center justify-center overflow-hidden rounded-xl sm:min-h-[260px]">
            {!hasVersion && !isCreating && <EmptyStage />}
            {generating && <GeneratingState status={status} />}
            {isDone && (
              <DoneState
                svg={svg}
                imageUrl={previewImageUrl}
                svgLoading={svgLoading}
              />
            )}
            {isFailed && <FailedState error={activeJob?.error ?? null} />}
          </div>
          {number && (
            <Sticker
              tone="lilac"
              className="absolute left-4 top-4 -rotate-3 sm:left-5 sm:top-5"
            >
              V{number}
            </Sticker>
          )}
          {isDone && previewOnly && (
            <PreviewBadge className="absolute right-5 top-5" />
          )}
        </div>

        {isDone && (
          <div className="w-full max-w-2xl lg:hidden">
            <LockInBar ws={ws} />
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyStage() {
  return (
    <div className="flex flex-col items-center px-8 text-center">
      <MascotArt pose="pencil" className="size-28" />
      <h2 className="mt-3 text-xl text-ink">Start your first version</h2>
      <p className="mt-1 max-w-xs text-sm font-semibold text-ink-soft">
        Describe your character below. Each prompt makes a new version you can
        come back to.
      </p>
    </div>
  );
}

function GeneratingState({ status }: { status: string }) {
  const currentIdx = Math.max(
    0,
    STAGES.findIndex((s) => s.status === status)
  );
  const progress = PROGRESS[status] ?? 8;

  return (
    <div
      className="flex w-full max-w-sm flex-col items-center gap-4 px-5 sm:gap-5 sm:px-6"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-end gap-2" aria-hidden="true">
        {[
          "bg-lilac-300 border-lilac-600",
          "bg-sun border-sun-edge",
          "bg-mint border-mint-edge",
        ].map((c, i) => (
          <span
            key={c}
            className={`size-4 rounded-full border-2 ${c}`}
            style={{
              animation: "dot-bounce 1.1s ease-in-out infinite",
              animationDelay: `${i * 0.15}s`,
            }}
          />
        ))}
      </div>

      <div className="h-4 w-full overflow-hidden rounded-full border-2 border-line-strong bg-paper">
        <div
          className="stripes h-full rounded-full bg-lilac-400 transition-[width] duration-700"
          style={{ width: `${progress}%` }}
        />
      </div>

      <ol className="w-full space-y-1.5">
        {STAGES.map((s, i) => {
          const state =
            i < currentIdx ? "done" : i === currentIdx ? "current" : "todo";
          return (
            <li
              key={s.status}
              className={cn(
                "flex items-center gap-2 text-sm font-bold",
                state === "todo" ? "text-ink-faint" : "text-ink"
              )}
            >
              <span
                className={cn(
                  "flex size-5 items-center justify-center rounded-full border-2",
                  state === "done" && "border-mint-edge bg-mint",
                  state === "current" && "border-lilac-500 bg-lilac-200",
                  state === "todo" && "border-line-strong bg-surface"
                )}
              >
                {state === "done" && (
                  <Check className="size-3" strokeWidth={3} />
                )}
                {state === "current" && <Spinner className="size-3" />}
              </span>
              {s.label}
            </li>
          );
        })}
      </ol>

      <p className="text-center text-xs font-semibold text-ink-soft">
        This usually takes a few minutes. You can leave this page — it keeps
        going without you.
      </p>
    </div>
  );
}

function DoneState({
  svg,
  imageUrl,
  svgLoading,
}: {
  svg: string | null;
  imageUrl: string | null;
  svgLoading: boolean;
}) {
  if (svgLoading && !svg) {
    return (
      <div className="flex flex-col items-center gap-3 text-ink-soft">
        <Spinner />
        <span className="text-xs font-bold">Loading preview…</span>
      </div>
    );
  }
  if (svg) {
    return (
      <InlineSvg
        svg={svg}
        className="h-full w-full animate-pop-in p-4 sm:p-6 [&>svg]:h-full [&>svg]:w-full"
      />
    );
  }
  if (imageUrl) {
    return (
      <MascotImage
        src={imageUrl}
        alt="Mascot"
        className="h-full w-full animate-pop-in object-contain p-4 sm:p-6"
      />
    );
  }
  return (
    <span className="text-xs font-bold text-ink-soft">
      Couldn't load the preview
    </span>
  );
}

function FailedState({ error }: { error: string | null }) {
  return (
    <div className="flex flex-col items-center px-8 text-center">
      <span className="flex size-14 items-center justify-center raised face-rose edge-rose-edge depth-3 rounded-2xl">
        <AlertTriangle className="size-6 text-ink" aria-hidden="true" />
      </span>
      <p className="mt-4 font-display text-lg font-semibold text-ink">
        This one didn't work out
      </p>
      <p className="mt-1 max-w-sm text-xs font-semibold text-ink-soft">
        {(error ?? "unknown error").split(":")[0]}
      </p>
      <p className="mt-3 text-sm font-semibold text-ink-soft">
        Tweak the prompt below and try again.
      </p>
    </div>
  );
}

/** Mobile-only call to action under the stage (desktop has it in the inspector). */
function LockInBar({ ws }: { ws: Workspace }) {
  const number = activeNumber(ws);
  if (ws.baseJobId === ws.activeRevisionId) return null;
  if (!ws.activeRevisionId) return null;

  return (
    <div className="flex items-center justify-between gap-3 raised face-mint edge-mint-edge depth-3 rounded-2xl py-2 pl-3.5 pr-2 sm:p-3">
      <p className="font-display text-base font-semibold text-ink">
        Love V{number}?
      </p>
      <LockInButton ws={ws} number={number} size="sm" />
    </div>
  );
}

function LockInButton({
  ws,
  number,
  className,
  size,
}: {
  ws: Workspace;
  number: number;
  className?: string;
  size?: "sm";
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="dark"
        size={size}
        data-coach="lock-in"
        className={cn("shrink-0", className)}
        onClick={() => setOpen(true)}
      >
        <Lock /> Use as my mascot
      </Button>
      <LockInDialog
        ws={ws}
        open={open}
        onOpenChange={setOpen}
        number={number}
      />
    </>
  );
}

function LockInDialog({
  ws,
  open,
  onOpenChange,
  number,
}: {
  ws: Workspace;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  number: number;
}) {
  const sub = useSubscription();
  const lock = actionAllowance(sub.data, "lock_base_mascot");

  async function handleLockIn() {
    if (!ws.activeRevisionId) return;
    try {
      await ws.lockIn(ws.activeRevisionId);
      onOpenChange(false);
      toastManager.add({
        title: `V${number} is your mascot`,
        description: "Now build its pack.",
        type: "success",
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 402) return;
      throw err;
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPopup>
        <DialogHeader>
          <DialogTitle>Lock in V{number} as your mascot?</DialogTitle>
          <DialogDescription className="font-semibold">
            Next you'll build its pack — poses and expressions of this exact
            character. This project's drafts are final after this; to explore a
            different look, start a new mascot.
          </DialogDescription>
          <div
            className={cn(
              "mt-2 flex items-center justify-between rounded-xl border-2 px-3 py-2.5 text-sm font-bold",
              lock.affordable
                ? "border-sun-edge bg-sun/40 text-ink"
                : "border-rose-edge bg-rose/50 text-ink"
            )}
          >
            <span className="inline-flex items-center gap-1.5">
              <Zap className="size-4 fill-current" aria-hidden="true" />
              {lock.free
                ? "Your first lock-in is free"
                : `Locking in uses ${lock.cost} credits`}
            </span>
            {sub.data && !lock.free && (
              <span className="text-ink-soft">You have {lock.balance}</span>
            )}
          </div>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Keep drafting
          </Button>
          {lock.affordable ? (
            <Button onClick={() => void handleLockIn()} loading={ws.isLocking}>
              <Lock /> Lock it in
            </Button>
          ) : (
            <Button variant="sun" render={<Link to="/settings/billing" />}>
              <Zap className="fill-current" /> Get credits
            </Button>
          )}
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}

function PromptBar({ ws }: { ws: Workspace }) {
  const [prompt, setPrompt] = useState("");
  const [billingLimitMessage, setBillingLimitMessage] = useState<string | null>(
    null
  );
  const sub = useSubscription();

  useEffect(() => {
    if (ws.activeRevision) {
      setPrompt(ws.activeRevision.prompt);
    }
  }, [ws.activeRevision?.id]);

  async function onSubmit() {
    if (!prompt.trim() || ws.isCreating) return;
    setBillingLimitMessage(null);
    try {
      await ws.generateRevision(prompt);
    } catch (err) {
      if (err instanceof ApiError && err.status === 402) {
        const body = err.body as {
          error?: string;
          kind?: "iterations" | "variations";
          limit?: number;
          resets_at?: string;
        } | null;
        setBillingLimitMessage(billingLimitToast(body).description);
        // A free_paused refusal: the refreshed entitlement shows its banner.
        void sub.refetch();
        return;
      }
      // Shown as a toast by the mutation; the prompt stays for editing.
      if (moderationErrorToast(err)) return;
      throw err;
    }
  }

  const isEditing = ws.activeRevision && prompt !== ws.activeRevision.prompt;
  const hasNoVersions = ws.revisions.length === 0;
  const nextNumber = ws.revisions.length + 1;
  const number = activeNumber(ws);
  const allowance = actionAllowance(
    sub.data,
    hasNoVersions ? "generate_character_draft" : "iterate_from_image"
  );

  return (
    <div className="shrink-0 border-t-2 border-line bg-paper px-3 pb-2 pt-2.5 sm:px-4 sm:pb-3 sm:pt-4">
      <div className="mx-auto max-w-2xl">
        {allowance.paused ? (
          <div className="mb-3 flex items-center justify-between gap-3 rounded-xl border-2 border-sun-edge bg-sun/50 px-3 py-2 text-sm font-bold text-ink">
            {freePausedMessage(allowance.resetsAt)}
            <Button
              size="xs"
              variant="dark"
              className="shrink-0"
              render={<Link to="/settings/billing" />}
            >
              Draw now with a plan
            </Button>
          </div>
        ) : (
          billingLimitMessage && (
            <div className="mb-3 flex items-center justify-between gap-3 rounded-xl border-2 border-rose-edge bg-rose/50 px-3 py-2 text-sm font-bold text-ink">
              {billingLimitMessage}
              <Button
                size="xs"
                variant="dark"
                render={<Link to="/settings/billing" />}
              >
                Get credits
              </Button>
            </div>
          )
        )}
        <PromptComposer
          value={prompt}
          onChange={setPrompt}
          onSubmit={() => void onSubmit()}
          placeholder="Describe your character, or list what to change — e.g. blue shirt, add a red scarf…"
          shortPlaceholder="Describe or list changes…"
          submitting={ws.isCreating}
          allowance={sub.data ? allowance : undefined}
          status={
            ws.activeRevision ? (
              isEditing ? (
                <span className="inline-flex items-center gap-1">
                  V{number} <ArrowRight className="size-3" /> V{nextNumber}
                </span>
              ) : (
                <span className="hidden sm:inline">
                  Edit the description or list changes — e.g. “blue shirt, add a
                  red scarf, remove the glasses”
                </span>
              )
            ) : undefined
          }
          submitLabel={
            hasNoVersions
              ? "Draw V1"
              : isEditing
                ? `Draw V${nextNumber}`
                : "Try again"
          }
        />
        <ContentRulesNote
          className="mt-1.5 text-center sm:mt-2"
          style={ws.project.style}
        />
      </div>
    </div>
  );
}

function LoadingShell() {
  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
      <div className="h-8 w-48 animate-pulse rounded-full bg-paper-deep" />
      <div className="mt-6 h-96 animate-pulse rounded-3xl bg-paper-deep" />
    </div>
  );
}

function NotFoundShell() {
  return (
    <div className="mx-auto max-w-md px-4 py-16">
      <div className="tactile p-8 text-center">
        <MascotArt pose="empty-box" className="mx-auto size-28" />
        <h1 className="mt-3 text-xl text-ink">Mascot not found</h1>
        <p className="mt-1 text-sm font-semibold text-ink-soft">
          It may have been deleted, or the link is wrong.
        </p>
        <Button className="mt-6" render={<Link to="/" />}>
          Back to my mascots
        </Button>
      </div>
    </div>
  );
}
